import { cn } from '@/lib/utils'
import { useOnErrorAuto } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { COMBINED_INFRINGEMENT_STATISTICS_QUERY } from '../queries'
import type { InfringementsTableFilter, SeverityCategory } from '../types'
import { useSeverityBackground } from '../util'
import { InfringementDetailCard } from './detail/detail-card'
import { useCombinedInfringementVariables } from './table/use-combined-infringement-variables'

const SEVERITIES: SeverityCategory[] = [
  'MINOR',
  'SERIOUS',
  'VERY_SERIOUS',
  'MOST_SERIOUS',
]
const STATUSES = ['OPEN', 'STARTED', 'CLOSED', 'ARCHIVED'] as const

interface Props {
  filter: InfringementsTableFilter
}

/**
 * Counts for everything the list's filters match, not only the loaded page.
 * Collapsed by default; the numbers are only fetched while it is open. Only
 * for readers who may see other drivers' infringements.
 */
export const InfringementStatistics: React.FC<Props> = ({ filter }) => {
  const translations = useTranslate({ title: 'infringements.statistics.title' })

  return (
    <InfringementDetailCard
      type="statistics"
      title={translations.title}
      icon="chart-simple"
      className="mb-4"
    >
      <StatisticsBody filter={filter} />
    </InfringementDetailCard>
  )
}

const StatisticsBody: React.FC<Props> = ({ filter }) => {
  const translations = useTranslate({
    total: 'infringements.statistics.total',
    drivers: 'infringements.statistics.drivers',
    byType: 'infringements.statistics.by-type',
    bySeverity: 'infringements.statistics.by-severity',
    byStatus: 'infringements.statistics.by-status',
    sources: {
      DRIVING_REST: 'infringements.source.driving-rest',
      WORKING_TIME: 'infringements.source.working-time',
      TACHOGRAPH_USE: 'infringements.source.tachograph-use',
    },
    severities: {
      MINOR: 'infringements.minor-infringement',
      SERIOUS: 'infringements.serious-infringement',
      VERY_SERIOUS: 'infringements.very-serious-infringement',
      MOST_SERIOUS: 'infringements.most-serious-infringement',
    },
    statuses: {
      OPEN: 'common.open-alt',
      STARTED: 'issues.started',
      CLOSED: 'common.closed',
      ARCHIVED: 'common.archived',
    },
  })
  const onErrorAuto = useOnErrorAuto()
  const getSeverityBackgroundColor = useSeverityBackground()
  const variables = useCombinedInfringementVariables(filter)

  const { data, loading } = useQuery(COMBINED_INFRINGEMENT_STATISTICS_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
    onError: onErrorAuto(),
  })
  const statistics = data?.combinedInfringementStatistics
  const count = (
    entries: { key: string; count: number }[] | undefined,
    key: string
  ) => entries?.find(entry => entry.key === key)?.count ?? 0

  const sources = variables.sources

  return (
    <div
      className={cn(
        'grid gap-3 p-5 sm:p-6 md:grid-cols-2 xl:grid-cols-4',
        loading && !statistics && 'opacity-60'
      )}
    >
      <Group title={translations.total}>
        <Figure value={statistics?.total} />
        <Line label={translations.drivers} value={statistics?.drivers} />
      </Group>

      <Group title={translations.byType}>
        {sources.map(source => (
          <Line
            key={source}
            label={translations.sources[source]}
            value={count(statistics?.bySource, source)}
          />
        ))}
      </Group>

      <Group title={translations.bySeverity}>
        {SEVERITIES.map(severity => (
          <Line
            key={severity}
            label={
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block size-2 rounded-full"
                  style={{
                    background: getSeverityBackgroundColor(severity),
                  }}
                />
                {translations.severities[severity]}
              </span>
            }
            value={count(statistics?.bySeverity, severity)}
          />
        ))}
      </Group>

      <Group title={translations.byStatus}>
        {STATUSES.map(status => (
          <Line
            key={status}
            label={translations.statuses[status]}
            value={count(statistics?.byStatus, status)}
          />
        ))}
      </Group>
    </div>
  )
}

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="flex flex-col gap-1.5 rounded-xl border border-stone-200 px-4 py-3">
    <h4 className="text-xs font-semibold text-stone-500 uppercase">{title}</h4>
    {children}
  </div>
)

const Figure: React.FC<{ value: number | undefined }> = ({ value }) => (
  <span className="text-3xl font-semibold text-stone-900 tabular-nums">
    {value ?? '–'}
  </span>
)

const Line: React.FC<{ label: React.ReactNode; value: number | undefined }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-3 text-sm">
    <span className="min-w-0 truncate text-stone-700">{label}</span>
    <span className="font-semibold text-stone-900 tabular-nums">
      {value ?? '–'}
    </span>
  </div>
)
